import { BlurFilter, Container, Graphics } from "pixi.js";
import { MousePluginType } from "../../plugins/mouse-input";
import BezierEasing from "@/client/bezier-easing";
import { InstanceRendering } from "@/client/renderer/index";
import { get_plugin } from "@/client/plugins";
import { Camera } from "@/client/camera";
import { get_stage_size } from "@/client/config/config";

export const MIN_MOUSE_DISTANCE = () => 25;
export const MAX_MOUSE_DISTANCE = (_stage_size: number) => {
  return 200;
};
export const RECONNECT_DISTANCE = (_stage_size: number) =>
  MIN_MOUSE_DISTANCE() + 175;

export function setup_mouse_effects() {
  const mouse_plugin = get_plugin("MOUSE") as MousePluginType | undefined;
  if (!mouse_plugin) {
    return null;
  }
  const dead_zone_indicator = new Graphics();

  dead_zone_indicator
    .circle(0, 0, MIN_MOUSE_DISTANCE())
    .fill({ color: 0xff0000, alpha: 0.5 });
  const line = new Graphics();
  line.lineTo(400, 400).stroke({ color: 0xff0000, width: 2, alpha: 0 });
  const container = new Container();
  const reconnect_indicator = new Graphics();
  reconnect_indicator.circle(0, 0, 25).fill({ color: 0xff0000, alpha: 0.0 });
  container.addChild(dead_zone_indicator, line, reconnect_indicator);

  return {
    container,
    dead_zone_indicator,
    line,
    reconnect_indicator,
    blur_filter_rec: new BlurFilter({ strength: 4 }),
    blur_filter: new BlurFilter({ strength: 1 }),
    mouse_ease: BezierEasing(0.61, -0.04, 0.58, 1),
    mouse_plugin,
  };
}

export function update_mouse_effects(renderer: InstanceRendering) {
  const mouse_effects = renderer.mouse_effects;
  if (!mouse_effects) {
    return;
  }
  mouse_effects.line.clear();
  mouse_effects.reconnect_indicator.clear();
  mouse_effects.dead_zone_indicator.clear();
  const canvas = document.getElementById("canvas");
  if (!canvas) {
    return;
  }

  if (!mouse_effects.mouse_plugin.plugin_options.mouse_state.is_active) {
    return;
  }

  const zoom = renderer.camera.zoom;
  const half_width = canvas.offsetWidth / 2;
  const half_height = canvas.offsetHeight / 2;
  const { offset_mouse_x, offset_mouse_y } = get_offset_mouse_from_camera(
    renderer.camera,
  );
  const mouse_x =
    mouse_effects.mouse_plugin.plugin_options.mouse_state.mouse_position.x;
  const mouse_y =
    mouse_effects.mouse_plugin.plugin_options.mouse_state.mouse_position.y;
  const mouse_control_origin_x = half_width - offset_mouse_x;
  const mouse_control_origin_y = half_height - offset_mouse_y;
  const distance = Math.sqrt(
    (mouse_x - mouse_control_origin_x) ** 2 +
      (mouse_y - mouse_control_origin_y) ** 2,
  );
  const dir = {
    x: (mouse_x - mouse_control_origin_x) / distance,
    y: (mouse_y - mouse_control_origin_y) / distance,
  };
  const stage_size = get_stage_size();
  const MIN_DISTANCE = MIN_MOUSE_DISTANCE();
  const MAX_DISTANCE = MAX_MOUSE_DISTANCE(stage_size);

  if (distance < MIN_DISTANCE) {
    return;
  }

  const width_pct = mouse_effects.mouse_ease(distance / MAX_DISTANCE);
  const line_org_x = mouse_control_origin_x + dir.x * MIN_DISTANCE;
  const line_org_y = mouse_control_origin_y + dir.y * MIN_DISTANCE;
  const angle_of_dir = Math.atan2(dir.y, dir.x);
  if (!mouse_effects.mouse_plugin.plugin_options.mouse_state.connected) {
    const percentage = Math.max(
      0,
      Math.min(1, (distance - MAX_DISTANCE - 50) / 100),
    );
    const RAD_START = Math.PI + Math.PI / 4;
    mouse_effects.reconnect_indicator
      .arc(
        mouse_x * zoom,
        mouse_y * zoom,
        25,
        angle_of_dir - RAD_START,
        angle_of_dir - RAD_START + Math.PI / 2,
      )
      .stroke({
        color: "#0041d0",
        alpha: 0.5 * percentage,
        width: 2,
        cap: "round",
      });
    mouse_effects.reconnect_indicator.filters = [mouse_effects.blur_filter_rec];
  } else {
    mouse_effects.line
      .moveTo(line_org_x * zoom, line_org_y * zoom)
      .lineTo(mouse_x * zoom, mouse_y * zoom)
      .stroke({
        color: "#0041d0",
        alpha: 0.5,
        cap: "round",
        width: 0.5 + (1 - width_pct) * 1.5,
      }).filters = [mouse_effects.blur_filter];
  }
}

function get_offset_mouse_from_camera(camera: Camera) {
  const entity_position = camera.get_last_entity_ref_position();
  const camera_iso = camera.camera_isometry;
  const offset_mouse_x = (camera_iso.x - entity_position.x) / camera.zoom;
  const offset_mouse_y = (camera_iso.y - entity_position.y) / camera.zoom;
  return { offset_mouse_x, offset_mouse_y };
}
